/**
 * Admin building blocks (idc-portal-design v1.1, AIC-180), shared by Analysts, Cycle, Settings and the Survey_list
 * reconciliation. The rail's nested Admin items are the secondary nav (components/Shell.tsx); these pages add:
 *   - AdminHero: the navy hero with the page's one question (kicker "Admin · …", h1, a note, optional right side).
 *   - useSave: one toast per save or transition (busy → done, or what failed with Try again), the busy flag for the
 *     trigger (aria-busy + aria-disabled, the label says what is happening) and the last error for inline context.
 *   - FieldError / Problem: a field-level message (role=alert when it appears) for the page's own validation.
 */
import { useState, type ReactNode } from "react";
import type { ApiError, PortalApi } from "../api/client.ts";
import { describeError } from "../api/hooks.ts";
import { useAttempt, useToast } from "../components/Toast.tsx";
import "./admin.css";

export function AdminHero({ section, title, note, aside, children }: { readonly section: string; readonly title: ReactNode; readonly note?: ReactNode; readonly aside?: ReactNode; readonly children?: ReactNode }) {
  return (
    <section className="ipd-hero adm-hero" aria-labelledby="adm-hero-h">
      <div className="adm-hero-main">
        <p className="ipd-hero-kicker">Admin · {section}</p>
        <h1 id="adm-hero-h">{title}</h1>
        {note ? <p className="adm-hero-note">{note}</p> : null}
        {children}
      </div>
      {aside ? <div className="adm-hero-aside">{aside}</div> : null}
    </section>
  );
}

/**
 * An error whose message is the whole explanation (a save that moved some of its work before it stopped): the error toast shows it
 * as written, without the "Nothing was changed." the other failures end with.
 */
export class SaveNote extends Error {
  constructor(message: string) { super(message); this.name = "SaveNote"; }
}

export interface SaveCopy<T> {
  /** The busy toast ("Saving the interviewer name…"). */
  readonly busy: string;
  /** The done toast; desc says what it means (never repeats the title). */
  readonly ok: (value: T) => { title: string; desc: string };
  /** The error toast's title ("The setting wasn't saved"). */
  readonly err: string;
}

/**
 * One operation, one toast: busy → ok, or err with Try again (it persists until dismissed or acted on). The call goes
 * through the toast module's useAttempt, so a 401 ends the session as everywhere else. Returns the value, or
 * undefined when it failed.
 */
export function useSave() {
  const attempt = useAttempt();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const run = async <T,>(copy: SaveCopy<NoInfer<T>>, fn: (api: PortalApi) => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    const t = toast.show({ tone: "busy", title: copy.busy });
    const out = await attempt(fn);
    setBusy(false);
    if (out.ok) {
      t.update({ tone: "ok", ...copy.ok(out.value) });
      return out.value;
    }
    setError(out.error);
    t.update({ tone: "err", title: copy.err, desc: out.error instanceof SaveNote ? out.error.message : `${describeError(out.error)} Nothing was changed.`, action: { label: "Try again", onAction: () => void run(copy, fn) } });
    return undefined;
  };
  return { busy, error, run, clearError: () => setError(null) };
}

/** Props for a trigger while its save runs: stays focusable, says it is busy, ignores clicks. */
export const busyProps = (busy: boolean) => ({ "aria-busy": busy || undefined, "aria-disabled": busy || undefined });

/** The page's own validation message under a form or field. */
export function Problem({ id, children }: { readonly id?: string; readonly children: ReactNode }) {
  return <p className="adm-problem" id={id} role="alert">{children}</p>;
}

/** What the server refused, in the form it came from (the toast says it too; this keeps it next to the fields). */
export function SaveError({ error, what }: { readonly error: ApiError | Error | null; readonly what: string }) {
  if (!error) return null;
  return <p className="adm-problem adm-problem--server" role="alert"><b>{what}</b> {describeError(error)}</p>;
}
